import { useEffect, useRef, useState } from "react";
import { Flex, Heading } from "@radix-ui/themes";
import Slider, { Settings } from "react-slick";

import AlbumCard from "@/components/ui/AlbumCard";
import { ListAlbumDto } from "@/lib/lutely-api/gen/dtos";
import { useAlbumsInfiniteList } from "@/lib/lutely-api/hooks/useAlbumsInfiniteList";
import { marketplaceTokens } from "@/lib/tokens";

import CarouselShell from "./CarouselShell";
import { useSliderSettings } from "./useSliderSettings";

type AlbumSliderSectionProps = Readonly<{
  query?: ReturnType<typeof useAlbumsInfiniteList>;
  albums?: ListAlbumDto[];
  title?: string;
  hideArrows?: boolean;
}>;

// Must stay in sync with the grid breakpoints in AlbumsMarketplacePage:
// grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5
function getGridCols(width: number): number {
  if (width >= 1280) return 5; // xl
  if (width >= 1024) return 4; // lg
  if (width >= 640) return 3; // sm
  return 2;
}

export default function AlbumSliderSection({
  query,
  albums: providedAlbums,
  title = "Albums",
  hideArrows,
}: AlbumSliderSectionProps) {
  const data = query?.data;
  const fetchNextPage = query?.fetchNextPage;
  const hasNextPage = query?.hasNextPage;
  const isFetchingNextPage = query?.isFetchingNextPage;
  const albums =
    providedAlbums ?? data?.pages.flatMap((page) => page.content) ?? [];
  const pageSize = (data?.pages[0]?.content.length ?? albums.length) || 20;
  const numAlbums = albums.length;

  const sliderRef = useRef<Slider | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(7);

  // Use viewport width for breakpoint-matched integer slidesToShow.
  // Integer slides are required — fractional slidesToShow causes react-slick
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280,
  );
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const slidesToShow = Math.min(getGridCols(viewportWidth), numAlbums);
  const slidesToScroll = Math.max(1, slidesToShow);

  const showRightFade =
    !hideArrows &&
    numAlbums > slidesToShow &&
    currentSlide + slidesToShow < numAlbums;

  useEffect(() => {
    if (!query || !fetchNextPage) {
      return;
    }

    if (currentSlide + itemsPerSlide >= numAlbums - pageSize) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  }, [
    currentSlide,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    itemsPerSlide,
    numAlbums,
    pageSize,
    query,
  ]);

  const settings: Settings = useSliderSettings({
    slidesToShow,
    slidesToScroll,
    beforeChange: (current: number, next: number) => {
      setItemsPerSlide(Math.abs(next - current));
    },
    afterChange: (current: number) => setCurrentSlide(current),
    arrows: !hideArrows,
    responsive: [],
  });

  if (albums.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" className="pt-[40px]">
      <Flex className="mb-[15px] items-center">
        <Heading as="h2" className={marketplaceTokens.sectionHeader}>
          {title}
        </Heading>
      </Flex>
      <CarouselShell
        showRightFade={showRightFade}
        className="marketplace-carousel--albums"
      >
        <Slider
          ref={sliderRef}
          {...settings}
          className={hideArrows ? "mobile-hide-arrows" : ""}
        >
          {albums.map((album) => (
            <AlbumCard key={album.albumId} album={album} />
          ))}
        </Slider>
      </CarouselShell>
    </Flex>
  );
}
